import { useState, useContext } from "react";
import { toast } from "sonner";
import axios from "axios";
import { SiteContext } from "@/App";
import { Loader2, Shield, Eye, EyeOff } from "lucide-react";

export default function SecurityTab({ token }) {
  const { API } = useContext(SiteContext);
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [saving, setSaving] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const handleChange = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm_password) {
      toast.error("New passwords do not match.");
      return;
    }
    if (form.new_password.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/change-password`, {
        current_password: form.current_password,
        new_password: form.new_password,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Password changed successfully.");
      setForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to change password.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 py-4">
      <div className="flex items-center gap-3 mb-2">
        <Shield size={18} className="text-verde" />
        <p className="font-manrope text-[10px] tracking-widest uppercase text-verde">Change Password</p>
      </div>

      <form onSubmit={handleChange} className="space-y-4 max-w-md">
        <div>
          <label className="font-manrope text-[10px] tracking-widest uppercase text-[#525252] block mb-2">Current Password</label>
          <div className="relative">
            <input
              data-testid="security-current-password"
              type={showCurrent ? "text" : "password"}
              value={form.current_password}
              onChange={e => setForm(p => ({ ...p, current_password: e.target.value }))}
              className="w-full bg-ink-800 border border-ink-600 focus:border-verde text-[#E5E5E5] font-manrope text-sm px-4 py-3 pr-12 rounded-sm outline-none transition-colors"
              required
            />
            <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-3 text-[#525252] hover:text-[#E5E5E5]">
              {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        <div>
          <label className="font-manrope text-[10px] tracking-widest uppercase text-[#525252] block mb-2">New Password</label>
          <div className="relative">
            <input
              data-testid="security-new-password"
              type={showNew ? "text" : "password"}
              value={form.new_password}
              onChange={e => setForm(p => ({ ...p, new_password: e.target.value }))}
              className="w-full bg-ink-800 border border-ink-600 focus:border-verde text-[#E5E5E5] font-manrope text-sm px-4 py-3 pr-12 rounded-sm outline-none transition-colors"
              placeholder="Min. 8 characters"
              required
            />
            <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-3 text-[#525252] hover:text-[#E5E5E5]">
              {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        <div>
          <label className="font-manrope text-[10px] tracking-widest uppercase text-[#525252] block mb-2">Confirm New Password</label>
          <input
            data-testid="security-confirm-password"
            type="password"
            value={form.confirm_password}
            onChange={e => setForm(p => ({ ...p, confirm_password: e.target.value }))}
            className="w-full bg-ink-800 border border-ink-600 focus:border-verde text-[#E5E5E5] font-manrope text-sm px-4 py-3 rounded-sm outline-none transition-colors"
            required
          />
        </div>

        <button
          data-testid="security-save-btn"
          type="submit"
          disabled={saving}
          className="w-full btn-verde py-3 disabled:opacity-60"
        >
          {saving ? <Loader2 size={14} className="animate-spin mx-auto" /> : "Update Password"}
        </button>
      </form>
    </div>
  );
}
